import React, {
  createContext,
  PropsWithChildren,
  useContext,
} from 'react'
import { ProcessedEvent, SelectedRange } from '../../types'
import { separateSingleAndMultiDayEvents } from '../../helpers/events'
import { MULTI_DAY_EVENT_HEIGHT } from '../../helpers/constants'
import { addDays } from 'date-fns'
import { WeekProps } from './types'
import { filterEventsWithinDays } from '../../helpers/eventFilters.ts'

export interface WeekProviderState {
  headerHeight: number
  weekDayDates: Date[]
  /**
   * These are events that start and finish
   * on the same day
   */
  singleDayEvents: ProcessedEvent[]
  /**
   * These are events that start on one day
   * and finish sometime on a future day
   */
  multiDayEvents: ProcessedEvent[]
}

interface WeekContextProps extends WeekProviderState {}

interface WeekProviderProps {
  week: WeekProps
  visibleRange: SelectedRange
  events: ProcessedEvent[]
}

const WeekContext = createContext<WeekContextProps>({} as any)

export function useWeekContext() {
  return useContext(WeekContext)
}

export class WeekProvider extends React.PureComponent<
  PropsWithChildren<WeekProviderProps>,
  WeekProviderState
> {
  constructor(props: WeekProviderProps) {
    super(props)
    this.state = this.calculateState()
  }

  componentDidUpdate(prevProps: Readonly<WeekProviderProps>) {
    const changed = Object.keys(this.props)
      .filter((k) => k !== 'children')
      .some(
        (k) => (this.props as any)[k] !== (prevProps as any)[k]
      )

    if (changed) {
      this.setState(this.calculateState())
    }
  }

  calculateState() {
    const weekDayDates = (
      this.props.week.weekDays ?? [0, 1, 2, 3, 4, 5, 6]
    ).map((d) => addDays(this.props.visibleRange.start, d))
    const eventsWithinDates = weekDayDates
      ? filterEventsWithinDays(this.props.events, weekDayDates)
      : this.props.events
    const separatedEvents = separateSingleAndMultiDayEvents(
      eventsWithinDates
    )
    return {
      headerHeight:
        separatedEvents.multiDayEvents.length *
          MULTI_DAY_EVENT_HEIGHT +
        45,
      weekDayDates,
      ...separatedEvents,
    }
  }

  render() {
    return (
      <WeekContext.Provider value={this.state}>
        {this.props.children}
      </WeekContext.Provider>
    )
  }
}
